// ============================================================
// issueWizardModel — the 9-step Olivium issuance wizard model (Phase 1, increment 4).
//
// This wizard drives the AUTHORITY=B path: it CONFIGURES a secure invitation and
// then calls the canonical Olivium proxy (POST /api/access/create → ss-invite-create).
// It is DISTINCT from the native sq_access_codes surface (CreateSecureAccess.tsx) —
// it never writes portal/session/consent/receipt tables from the browser, never
// fabricates a link or code, and displays returned values only when genuinely created.
//
// Provider-gated: with the external engine unconfigured the final live issue fails
// closed honestly (PROVIDER_REQUIRED); a dry-run preview is always safe (flagged mock).
// ============================================================

import type { AccessCodeTargetType } from '../../../../api/client'
import { SECURE_TARGET_TYPES, type SecureTargetType } from './secureAccessData'

export const WIZARD_STEPS = [
  { id: 'recipient', title: 'Recipient' },
  { id: 'experience', title: 'Experience' },
  { id: 'target', title: 'Target' },
  { id: 'permissions', title: 'Permissions' },
  { id: 'security', title: 'Security' },
  { id: 'nda', title: 'NDA & consent' },
  { id: 'notifications', title: 'Notifications' },
  { id: 'delivery', title: 'Delivery' },
  { id: 'confirm', title: 'Confirm' },
] as const
export type WizardStepId = (typeof WIZARD_STEPS)[number]['id']

// Audience (WHO the recipient is) — kept SEPARATE from OrgRole/permissions.
export const AUDIENCES = ['prospective_client', 'client', 'collaborator', 'trainee', 'test_user'] as const
export type Audience = (typeof AUDIENCES)[number]

export const EXPERIENCES = [
  'demo_review_portal', 'client_workspace', 'collaborator_workspace', 'trainee_workspace',
  'test_user_portal', 'secure_document_viewer', 'project_room', 'artifact_review', 'meeting_workspace',
] as const
export type Experience = (typeof EXPERIENCES)[number]

export const PERMISSIONS = [
  'view', 'comment', 'annotate', 'approve', 'request_revision',
  'upload', 'message', 'join_meeting', 'complete_assigned_tasks', 'request_export',
] as const
export type Permission = (typeof PERMISSIONS)[number]

export const EXPIRATIONS = ['15m', '1h', '24h', '7d'] as const
export type Expiration = (typeof EXPIRATIONS)[number]

export const DELIVERIES = ['issue_and_copy_link', 'issue_without_sending', 'send_through_provider'] as const
export type Delivery = (typeof DELIVERIES)[number]

export const SECURITY_CONTROLS = [
  { key: 'fallbackRecovery', label: 'Fallback recovery code' },
  { key: 'browserBinding', label: 'Bind to first browser' },
  { key: 'stepUp', label: 'Step-up verification' },
  { key: 'restrictDownload', label: 'Restrict download' },
  { key: 'restrictPrint', label: 'Restrict print' },
  { key: 'restrictCopy', label: 'Restrict copy' },
  { key: 'watermark', label: 'Watermark' },
  { key: 'exportApproval', label: 'Require export approval' },
] as const
export type SecurityKey = (typeof SECURITY_CONTROLS)[number]['key']

export interface WizardForm {
  // 1 · recipient
  recipientName: string
  recipientEmail: string
  organization: string
  relationship: Audience
  existingAccount: boolean
  // 2 · experience
  experience: Experience
  // 3 · target
  targetType: '' | AccessCodeTargetType
  targetId: string
  targetLabel: string
  // 4 · permissions (least privilege by default)
  permissions: Permission[]
  // 5 · security
  expiration: Expiration
  maxUses: string
  sessionMinutes: string
  security: Record<SecurityKey, boolean>
  // 6 · NDA & consent
  ndaRequired: boolean
  ndaVersion: string
  monitoringDisclosure: boolean
  // 7 · notifications (portal is essential + always on)
  notifyEmail: boolean
  // 8 · delivery
  delivery: Delivery
  // meta
  live: boolean
}

export const DEFAULT_FORM: WizardForm = {
  recipientName: '', recipientEmail: '', organization: '', relationship: 'prospective_client', existingAccount: false,
  experience: 'demo_review_portal',
  targetType: '', targetId: '', targetLabel: '',
  permissions: ['view'], // least privilege
  expiration: '24h', maxUses: '1', sessionMinutes: '720',
  security: {
    fallbackRecovery: true, browserBinding: true, stepUp: false, restrictDownload: false,
    restrictPrint: false, restrictCopy: false, watermark: false, exportApproval: false,
  },
  ndaRequired: false, ndaVersion: 'v1', monitoringDisclosure: false,
  notifyEmail: false,
  delivery: 'issue_and_copy_link',
  live: false,
}

// Map an experience onto the access_type vocabulary accepted by /api/access/create.
const EXPERIENCE_ACCESS_TYPE: Record<Experience, string> = {
  demo_review_portal: 'portal_session', client_workspace: 'workspace', collaborator_workspace: 'workspace',
  trainee_workspace: 'workspace', test_user_portal: 'portal_session', secure_document_viewer: 'file',
  project_room: 'workspace', artifact_review: 'deliverable', meeting_workspace: 'call',
}
// Map audience onto the role vocabulary accepted by /api/access/create (least-priv default).
const AUDIENCE_ROLE: Record<Audience, string> = {
  prospective_client: 'viewer', client: 'client_owner', collaborator: 'client_collaborator',
  trainee: 'viewer', test_user: 'viewer',
}

export function targetTypeMeta(v: string): SecureTargetType | undefined {
  return SECURE_TARGET_TYPES.find((t) => t.value === v)
}

// Per-step validation — forward progress is gated on the current step being valid.
export function stepValid(step: WizardStepId, form: WizardForm): boolean {
  switch (step) {
    case 'recipient':
      // A name + a verified identifier (email) are required.
      return form.recipientName.trim().length > 0 && /.+@.+\..+/.test(form.recipientEmail.trim())
    case 'target':
      // A bound target requires a target id — "no code without a destination".
      return !form.targetType || form.targetId.trim().length > 0
    case 'permissions':
      return form.permissions.length > 0
    default:
      return true
  }
}

export function firstInvalidStepIndex(form: WizardForm): number {
  for (let i = 0; i < WIZARD_STEPS.length; i++) {
    if (!stepValid(WIZARD_STEPS[i].id, form)) return i
  }
  return -1
}

// The payload for POST /api/access/create. The Olivium proxy currently forwards
// { project_id, label } to ss-invite-create and treats the richer fields as
// operator metadata — we send them so intent + audit are captured, and encode the
// full configuration + idempotency key in `notes`. Nothing here is a grant; the
// engine is the authority.
export interface OliviumCreatePayload {
  project_id: string
  label: string
  live: boolean
  recipient: string
  access_type: string
  role: string
  visibility: string
  expiration: string
  mode: string
  notes: string
}

export function buildCreatePayload(form: WizardForm, project_id: string, idempotencyKey: string): OliviumCreatePayload {
  const label = `${form.recipientName.trim()} · ${form.experience}`.slice(0, 120)
  const mode = form.relationship === 'test_user' ? 'test_url' : 'live_client'
  const notes = JSON.stringify({
    idempotency_key: idempotencyKey,
    audience: form.relationship,
    experience: form.experience,
    organization: form.organization || null,
    existing_account: form.existingAccount,
    target: form.targetType ? { type: form.targetType, id: form.targetId, label: form.targetLabel || null } : null,
    permissions: form.permissions,
    security: form.security,
    limits: { expiration: form.expiration, max_uses: Number(form.maxUses) || 1, session_minutes: Number(form.sessionMinutes) || 720 },
    nda: { required: form.ndaRequired, version: form.ndaRequired ? form.ndaVersion : null, monitoring_disclosure: form.monitoringDisclosure },
    notifications: { portal: true, email: form.notifyEmail },
    delivery: form.delivery,
  })
  return {
    project_id,
    label,
    live: form.live,
    recipient: form.recipientEmail.trim(),
    access_type: EXPERIENCE_ACCESS_TYPE[form.experience],
    role: AUDIENCE_ROLE[form.relationship],
    visibility: 'client_visible',
    expiration: form.expiration,
    mode,
    notes,
  }
}

// The wizard's honest lifecycle status vocabulary.
export type WizardStatus = 'DRAFT' | 'VALIDATING' | 'ISSUING' | 'ISSUED' | 'PROVIDER_REQUIRED' | 'FAILED'

// The engine's fail-closed codes we treat as "provider not wired" rather than a
// user error, so the UI can be honest instead of blaming the operator.
export const PROVIDER_REQUIRED_CODES = new Set([
  'live_not_configured', 'live_mapping_missing', 'live_not_connected', 'live_identity_missing',
])
